import React, { useState, useEffect, useRef, memo } from "react"
import { Box, Flex } from "@chakra-ui/react"
import { useRecoilValue } from "recoil"
import styled from "@emotion/styled"

import type { Room } from "../../../recoil/atoms/room/types"
import { brandingSelector } from "../../../recoil/atoms/room"
import { viewDependingLayoutAtom } from "../../../recoil/atoms/view"
import useWindowSize from "../../../hooks/useWindowSize"

type StyledProps = {
  room?: Room
  scrollPosition?: "top" | "bottom" | "middle" | "noHasScrollbar"
}

const ScrollContainer = styled(Flex)<StyledProps>`
  -webkit-align-content: flex-start;
  align-content: flex-start;
  --scrollbar-width-product-description: 1px;
  --mask-height-product-description: 30px;
  overflow-y: auto;
  --mask-size-content-product-description: calc(
      100% - var(--scrollbar-width-product-description)
    )
    100%;
  --mask-image-scrollbar-product-description: linear-gradient(black, black);
  --mask-size-scrollbar-product-description: var(
      --scrollbar-width-product-description
    )
    100%;

  mask-image: ${({ scrollPosition }) =>
    scrollPosition === "noHasScrollbar"
      ? ""
      : `linear-gradient(
          to ${scrollPosition === "top" ? "top" : "bottom"},
          transparent,
          black var(--mask-height-product-description),
          black calc(100% - ${
            scrollPosition === "middle"
              ? "var(--mask-height-product-description)"
              : "0px"
          }),
          transparent
        ),
        var(--mask-image-scrollbar-product-description)`};

  mask-size: var(--mask-size-content-product-description),
    var(--mask-size-scrollbar-product-description);
  mask-position: 0 0, 100% 0;
  mask-repeat: no-repeat, no-repeat;

  &::-webkit-scrollbar-track {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorder || "#cbd5e0"};
    border-radius: 10px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ roomStyles }) =>
      roomStyles.servicePanelElementBorderOffset || "#fff"};
    border-radius: 10px;
  }
`

type ScrollableBlurListProps<T> = {
  isMobile?: boolean
  isTransparent?: boolean
  items?: T[]
  renderItem?: (value: T, index: number, array: T[]) => React.ReactNode
  renderListHeader?: () => React.ReactNode
  renderEmptyState?: () => React.ReactNode
  paddingBottom?: `${string}px`
}

const ScrollableBlurList = <T,>({
  isMobile,
  isTransparent,
  renderItem,
  renderListHeader,
  renderEmptyState,
  items = [],
  paddingBottom = "0px" // "70px" for single tab on desktop
}: ScrollableBlurListProps<T>) => {
  const { screenSize } = useWindowSize()
  const roomStyles = useRecoilValue(brandingSelector)
  // const [firstOpen, setFirstOpen] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { isLandscape } = useRecoilValue(viewDependingLayoutAtom)

  const [scrollPosition, setPositionScroll] =
    useState<StyledProps["scrollPosition"]>("noHasScrollbar")

  useEffect(() => {
    // This useEffect handle resize event, depending upon that apply blur effect on the list
    if (isMobile) {
      const videosContainer = scrollContainerRef.current
      const observer = new ResizeObserver(entries => {
        for (let entry of entries) {
          if (videosContainer.scrollHeight <= videosContainer.clientHeight) {
            setPositionScroll("noHasScrollbar")
          } else {
            if (videosContainer.scrollTop < 10) {
              setPositionScroll("top")
            } else if (
              videosContainer?.scrollTop >=
              videosContainer?.scrollHeight - videosContainer?.clientHeight - 10
            ) {
              setPositionScroll("bottom")
            } else {
              setPositionScroll("middle")
            }
          }
        }
      })

      if (videosContainer) {
        observer.observe(videosContainer)
      }

      return () => {
        if (videosContainer) {
          observer.unobserve(videosContainer)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollContainerRef, isTransparent])

  useEffect(() => {
    // This useEffect handle scroll event, depending upon that apply blur effect on the list
    if (!isMobile) return

    const scrollContainer = scrollContainerRef.current
    if (!scrollContainer) return

    const handleScroll = () => {
      if (scrollContainer.scrollTop < 10) {
        // Scroll position is on top
        setPositionScroll("top")
      } else if (
        scrollContainer?.scrollTop >=
          scrollContainer?.scrollHeight - scrollContainer?.clientHeight - 10 &&
        scrollContainer.scrollTop !== 0
      ) {
        setPositionScroll("bottom")
      } else {
        if (scrollContainer.scrollTop !== 0) {
          setPositionScroll("middle")
        }
      }
    }

    scrollContainer.addEventListener("scroll", handleScroll, false)
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTransparent])

  // const showInBottom = async () => {
  //   setFirstOpen(true)
  //   const container = (await waitForElm("")) as HTMLDivElement

  //   container.scrollTop = container.scrollHeight
  //   setFirstOpen(false)
  // }

  // useEffect(() => {
  //   if (isLandscape) {
  //     let timerForScroll = setTimeout(() => {
  //       showInBottom()
  //     }, 300)
  //     return () => clearTimeout(timerForScroll)
  //   }
  // }, [isLandscape])

  // useEffect(() => {
  //   if (isMobile) {
  //     if (targetTabIndex === 0) {
  //       setFirstOpen(true)
  //     }

  //     if (targetTabIndex === 1) {
  //       scrollContainerRef.current.scrollTop =
  //         scrollContainerRef.current.scrollHeight

  //       let timer = setTimeout(() => {
  //         setFirstOpen(false)
  //       }, 1000)
  //       return () => clearTimeout(timer)
  //     }
  //   }
  // }, [targetTabIndex, isTransparent, isLandscape, isMobile])

  return (
    <Box
      p={isMobile ? "0rem" : "1rem 0.5rem 1rem 0"}
      height="100%"
      overflowX="hidden"
      overflowY="hidden"
      paddingBottom={paddingBottom} // 70px to make same as chat list
    >
      <ScrollContainer
        flexDir="column"
        overflow="auto"
        scrollPosition={scrollPosition}
        ref={scrollContainerRef}
        zIndex={isMobile ? "10000" : "unset"}
        width={"100%"}
        height="100%"
        padding={isMobile ? "8px" : "0 0.5rem 0 1rem"}
        roomStyles={roomStyles}
        gap={isMobile ? "8px" : "0.6rem"}
        // style={firstOpen ? {} : { scrollBehavior: "smooth" }}
        className="hide-scrollbar"
      >
        {items.length === 0 && !isTransparent && renderEmptyState?.()}

        {renderListHeader?.()}

        {items.map(renderItem)}
      </ScrollContainer>
    </Box>
  )
}

export default ScrollableBlurList
